/**
 * F-601-FE-02: SentryErrorBoundary.tsx
 * Wraps children with Sentry.ErrorBoundary to capture React component errors.
 * Displays a user-friendly fallback UI when a component tree crashes.
 */

import type { ReactNode } from 'react';
import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// Warning icon (inline SVG — no external dependency)
// ---------------------------------------------------------------------------

function WarningIcon() {
  return (
    <svg
      className="w-12 h-12 text-clinical-amber mx-auto"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Fallback UI
// ---------------------------------------------------------------------------

function ErrorFallback() {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div
      className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center px-4"
      role="alert"
      aria-live="assertive"
    >
      <div className="w-full max-w-sm bg-white rounded-clinical shadow-clinical-md border border-clinical-border p-8 text-center">
        <WarningIcon />

        <h1 className="mt-4 font-heading text-xl font-semibold text-clinical-charcoal">
          Something went wrong
        </h1>

        <p className="mt-2 font-body text-sm text-clinical-text-muted leading-relaxed">
          An unexpected error occurred. Please refresh the page to continue. If the problem
          persists, contact your system administrator.
        </p>

        <button
          type="button"
          onClick={handleRefresh}
          className="
            mt-6 w-full
            inline-flex items-center justify-center
            min-h-[44px] px-6
            rounded-clinical
            bg-clinical-teal text-white
            font-body font-semibold text-sm
            hover:bg-clinical-teal/90
            transition-colors duration-150
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2
          "
        >
          Refresh page
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SentryErrorBoundary
// ---------------------------------------------------------------------------

interface Props {
  children: ReactNode;
}

/**
 * Wrap the application (or a subtree) with this boundary to capture React
 * rendering errors in Sentry and show a graceful fallback to the user.
 *
 * Usage:
 *   <SentryErrorBoundary>
 *     <App />
 *   </SentryErrorBoundary>
 */
export function SentryErrorBoundary({ children }: Props) {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
